<script lang="ts">
export interface TableColumn {
  title: string;
  filter?: TableColumnFilter;
  alignRight?: boolean;
  noSort?: boolean;
}

export type TableColumnFilter =
  | TableColumnFilterFreeSearch
  | TableColumnFilterDateRange
  | TableColumnFilterMultiValue
  | TableColumnFilterResetButton;

export type TableColumnFilterFreeSearch = {
  id?: string;
  type: Extract<FilterType, 'FREE_SEARCH'>;
  placeholder?: string;
  initialValue?: string;
};

export type TableColumnFilterDateRange = {
  id?: string;
  type: Extract<FilterType, 'DATE_RANGE'>;
  placeholder?: string;
  initialValue?: number;
};

export type TableColumnFilterMultiValue = {
  id?: string;
  type: Extract<FilterType, 'MULTI_VALUE'>;
  selectOptions: InstanceType<typeof ElInputSelect>['$props']['options'];
  initialValue?: string;
};

export type TableColumnFilterResetButton = {
  type: 'RESET_FILTERS_BUTTON';
};

export interface DataControls {
  sortStatus: SortStatus;
  paginationStatus: {
    currentPage: number;
  };
  filterStatus: FilterStatus[];
  triggeredEvent: 'paginate' | 'filter' | 'sort' | 'firstLoad' | 'trigger';
}

export interface SortStatus {
  columnToSort: number;
  order: 'asc' | 'desc';
}

export interface ServerSideTableProps {
  columns: TableColumn[];
  sortByCol?: number; // initial sort by column
  sortByColAsc?: boolean; // initial sort by column should be in Ascending order
  noFilters?: boolean;
  noFooter?: boolean;
  rowsSelectionMode?: 'single' | 'multiple';
  rowsSelectionDisabled?: boolean;
  rowsPerPage?: number;
  dataController: (args: DataControls) => Promise<{ data: DataRow[]; totalRows: number }>;
}

export type FilterStatus = TextFilterStatus | DateFilterStatus;

export type TextFilterStatus = {
  id?: string;
  textSearch: string;
  columnTitle: string;
  type: Extract<FilterType, 'FREE_SEARCH' | 'MULTI_VALUE'>;
};

export type DateFilterStatus = {
  id?: string;
  dateFrom: number;
  columnTitle: string;
  type: Extract<FilterType, 'DATE_RANGE'>;
};
</script>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import ElServerSideTablePagination from '@/table/ElServerSideTablePagination.vue';
import CustomTransition from '@/_CustomTransition.vue';
import ElInputCheckbox from '@/forms/ElInputCheckbox.vue';
import ElInputSelect from '@/forms/ElInputSelect.vue';
import ElInputDate from '@/forms/ElInputDate.vue';
import ElInputText from '@/forms/ElInputText.vue';
import ElSpinner from '@/ElSpinner.vue';
import ElTableCell from '@/table/ElTableCell.vue';
import ElButton from '@/ElButton.vue';
import { watchDeep, useDebounceFn } from '@vueuse/core';
import { DataRow, FilterType } from '@/table/commonTypes';

const props = withDefaults(defineProps<ServerSideTableProps>(), {
  sortByCol: -1,
  sortByColAsc: false,
  rowsSelectionMode: undefined,
  rowsPerPage: 15,
});

const emit = defineEmits<{
  (event: 'rows-selected', rows: DataRow[]): void;
  (event: 'newPageSelected', pageNumber: number): void;
}>();

const loading = ref(true);

const MAX_COLS = 10;

const currentPageDataRows = ref<DataRow[]>([]);

const pagination = ref<InstanceType<typeof ElServerSideTablePagination> | null>(null);

onMounted(async () => {
  const newData = await props.dataController({
    paginationStatus: {
      currentPage: 0,
    },
    sortStatus: currentSortStatus.value,
    filterStatus: filterStatus.value,
    triggeredEvent: 'firstLoad',
  });
  currentPageDataRows.value = newData.data;
  totalRowsCount.value = newData.totalRows;
  loading.value = false;
});

function usePagination() {
  const totalRowsCount = ref(0);
  const currentPage = computed(() => pagination.value?.currentPage ?? 0);

  const goToFirstPage = () => pagination.value?.goToPage(0, { doNotEmitEvent: true });

  const newPageSelected = async (newPage: number) => {
    loading.value = true;
    currentPageDataRows.value = (
      await props.dataController({
        paginationStatus: {
          currentPage: newPage,
        },
        sortStatus: currentSortStatus.value,
        filterStatus: filterStatus.value,
        triggeredEvent: 'paginate',
      })
    ).data;
    loading.value = false;
  };

  return {
    newPageSelected,
    totalRowsCount,
    currentPage,
    goToFirstPage,
  };
}

function useSorting() {
  const sortStatus = ref<{
    currentlySortColIndex: number;
    arrows: boolean[];
  }>({
    currentlySortColIndex: props.sortByCol > -1 ? props.sortByCol : -1,
    arrows: [...new Array(MAX_COLS)].map((v, i) => (i === props.sortByCol ? props.sortByColAsc : v)),
  });

  const resetSort = () => {
    sortStatus.value = {
      currentlySortColIndex: -1,
      arrows: [],
    };
  };
  const currentSortStatus = computed<DataControls['sortStatus']>(() => {
    return {
      columnToSort: sortStatus.value.currentlySortColIndex,
      order: sortStatus.value.arrows[sortStatus.value.currentlySortColIndex] ? 'asc' : 'desc',
    };
  });

  const toggleSortArrow = async (colIndex: number) => {
    sortStatus.value.currentlySortColIndex = colIndex;
    sortStatus.value.arrows.splice(colIndex, 1, !sortStatus.value.arrows[colIndex]);
    loading.value = true;
    try {
      const newData = await props.dataController({
        paginationStatus: {
          currentPage: currentPage.value,
        },
        sortStatus: currentSortStatus.value,
        filterStatus: filterStatus.value,
        triggeredEvent: 'sort',
      });
      currentPageDataRows.value = newData.data;
      totalRowsCount.value = newData.totalRows;
    } catch (error) {
      // TODO handle error
      console.error(error);
    } finally {
      loading.value = false;
    }
  };

  return {
    sortStatus,
    toggleSortArrow,
    currentSortStatus,
    resetSort,
  };
}

function useFilters() {
  const filterStatus = ref<FilterStatus[]>([]);
  const isResettingFilters = ref(false);

  onMounted(() => {
    resetFilters();
    watchDeep(filterStatus, async filters => {
      if (isResettingFilters.value) {
        applyFilters(filters);
        isResettingFilters.value = false;
      } else {
        applyFiltersDebounced(filters);
      }
    });
  });

  const resetFilters = () => {
    isResettingFilters.value = true;

    filterStatus.value = new Array(MAX_COLS);
    for (let i = 0; i < MAX_COLS; i++) {
      const filter = props.columns[i]?.filter;
      if (filter) {
        if (filter.type === 'FREE_SEARCH') {
          filterStatus.value.splice(i, 1, {
            id: filter.id,
            textSearch: filter.initialValue ?? '',
            type: 'FREE_SEARCH',
            columnTitle: props.columns[i].title,
          });
        } else if (filter.type === 'MULTI_VALUE') {
          filterStatus.value.splice(i, 1, {
            id: filter.id,
            textSearch: filter.initialValue ?? '',
            type: 'MULTI_VALUE',
            columnTitle: props.columns[i].title,
          });
        } else if (filter.type === 'DATE_RANGE') {
          filterStatus.value.splice(i, 1, {
            id: filter.id,
            dateFrom: filter.initialValue ?? 0,
            type: 'DATE_RANGE',
            columnTitle: props.columns[i].title,
          });
        }
      }
    }
  };

  const applyFilters = async (filters: FilterStatus[]) => {
    console.log('applyFilters');
    loading.value = true;
    goToFirstPage();
    const newData = await props.dataController({
      paginationStatus: {
        currentPage: currentPage.value,
      },
      sortStatus: currentSortStatus.value,
      filterStatus: filters,
      triggeredEvent: 'filter',
    });
    currentPageDataRows.value = newData.data;
    totalRowsCount.value = newData.totalRows;
    loading.value = false;
  };

  const applyFiltersDebounced = useDebounceFn(applyFilters, 500);

  return {
    filterStatus,
    resetFilters,
  };
}

function useRowsSelection() {
  const selectedRows = ref<typeof currentPageDataRows.value>([]);

  const emitRowsSelected = () => {
    emit('rows-selected', selectedRows.value);
  };

  const rowsSelected = (rows: DataRow[]) => {
    selectRows(rows.map(r => r.id));
  };

  const rowUnselected = (rowId: string) => {
    const index = selectedRows.value.findIndex(r => r.id === rowId);
    selectedRows.value.splice(index, 1);
    emitRowsSelected();
  };

  // select the rows with the given ids, possibly in addition to the rows that are already selected
  const selectRows = async (rowsIds: string[]) => {
    if (props.rowsSelectionMode === 'single') {
      selectedRows.value = [];
      await nextTick();
    }
    const rowsToSelect = currentPageDataRows.value.filter(r => rowsIds.includes(r.id));
    if (props.rowsSelectionMode === 'single') {
      rowsToSelect.length = Math.min(1, rowsToSelect.length);
    }

    for (const row of rowsToSelect) {
      if (!selectedRows.value.some(r => r.id === row.id)) {
        selectedRows.value.push(row);
      }
    }

    emitRowsSelected();
  };

  const unselectAllRows = () => {
    selectedRows.value = [];
    emitRowsSelected();
  };

  const getDataRows = (): DataRow[] => currentPageDataRows.value;
  const getSelectedRows = (): DataRow[] => selectedRows.value;

  const allRowsSelected = computed(() => {
    return selectedRows.value.length > 0 && selectedRows.value.length === currentPageDataRows.value.length;
  });

  return {
    selectedRows,
    rowsSelected,
    rowUnselected,
    selectRows,
    unselectAllRows,
    getDataRows,
    getSelectedRows,
    allRowsSelected,
  };
}

const { selectedRows, rowsSelected, rowUnselected, selectRows, unselectAllRows, getDataRows, getSelectedRows, allRowsSelected } =
  useRowsSelection();
const { filterStatus, resetFilters } = useFilters();
const { sortStatus, toggleSortArrow, currentSortStatus, resetSort } = useSorting();
const { newPageSelected, totalRowsCount, currentPage, goToFirstPage } = usePagination();

const resetAllAndFetch = async () => {
  // Resetting filters and sort will trigger the dataController
  resetSort();
  resetFilters();
  unselectAllRows();
};

const triggerDataController = async () => {
  loading.value = true;
  const newData = await props.dataController({
    paginationStatus: {
      currentPage: currentPage.value,
    },
    sortStatus: currentSortStatus.value,
    filterStatus: filterStatus.value,
    triggeredEvent: 'trigger',
  });
  currentPageDataRows.value = newData.data;
  totalRowsCount.value = newData.totalRows;
  loading.value = false;
};

watch(
  () => props.columns,
  () => resetFilters(),
  { deep: true, immediate: true },
);

defineExpose({
  selectRows,
  resetAllAndFetch,
  unselectAllRows,
  getDataRows,
  getSelectedRows,
  triggerDataController,
});
</script>

<template>
  <div class="flex flex-col">
    <div class="overflow-x-auto">
      <div class="inline-block min-w-full bg-white align-middle">
        <table class="min-w-full">
          <thead>
            <tr class="bg-neutral-surface-raised font-semibold">
              <th
                v-if="rowsSelectionMode"
                scope="col"
                class="bg-neutral-surface-raised text-neutral-darker flex justify-center px-3 py-3 text-xs uppercase leading-4 tracking-wider"
                :class="{ 'cursor-pointer': rowsSelectionMode === 'multiple' }"
              >
                <ElInputCheckbox
                  v-if="rowsSelectionMode === 'multiple'"
                  :model-value="allRowsSelected"
                  :disabled="rowsSelectionDisabled"
                  size="xxs"
                  @click="() => (allRowsSelected ? unselectAllRows() : rowsSelected(currentPageDataRows))"
                />
                <div v-else class="w-7" />
              </th>
              <th
                v-for="(col, index) in props.columns"
                :key="index"
                scope="col"
                class="bg-neutral-surface-raised text-neutral-darker whitespace-nowrap rounded-none p-4 text-xs font-semibold uppercase leading-4 tracking-wider"
                :class="[col.alignRight ? 'text-right' : 'text-left', !col.noSort ? 'cursor-pointer' : 'cursor-default']"
                :title="!col.noSort ? (sortStatus.arrows[index] ? 'Ordine crescente' : 'Ordine decrescente') : 'Colonna non ordinabile'"
                @click="!col.noSort && toggleSortArrow(index)"
              >
                {{ col.title }}

                <!-- //////////////////// SORT //////////////////////////// -->
                <svg
                  v-if="!col.noSort"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  class="ml-1 inline-flex h-3 w-3"
                  fill="currentColor"
                >
                  <path
                    v-if="sortStatus.arrows[index]"
                    d="M13 5.41V21a1 1 0 0 1-2 0V5.41l-5.3 5.3a1 1 0 1 1-1.4-1.42l7-7a1 1 0 0 1 1.4 0l7 7a1 1 0 1 1-1.4 1.42L13 5.4z"
                  />
                  <path
                    v-else
                    d="M11 18.59V3a1 1 0 0 1 2 0v15.59l5.3-5.3a1 1 0 0 1 1.4 1.42l-7 7a1 1 0 0 1-1.4 0l-7-7a1 1 0 0 1 1.4-1.42l5.3 5.3z"
                    title="Ordine Decrescente"
                  />
                </svg>
              </th>
            </tr>
            <!-- ///////////////////// FILTERS ///////////////////////// -->
            <tr v-if="filterStatus.length > 0 && !noFilters">
              <th v-if="rowsSelectionMode" scope="col" />
              <th
                v-for="(col, index) in columns"
                :key="index"
                scope="col"
                class="text-neutral-darker px-3 py-3 text-left text-xs font-light leading-4 tracking-wider"
              >
                <!-- FILTER: TEXT SEARCH -->
                <ElInputText
                  v-if="col.filter?.type === 'FREE_SEARCH' && filterStatus[index].type === 'FREE_SEARCH'"
                  v-model="(filterStatus[index] as TextFilterStatus).textSearch"
                  :placeholder="col.filter?.placeholder || 'Cerca'"
                  :hidden-error-message="true"
                  :leading-icon="{
                    name: 'MagnifyingGlassIcon',
                  }"
                />

                <!-- FILTER: DATE RANGE -->
                <div v-else-if="col.filter?.type === 'DATE_RANGE' && filterStatus[index].type === 'DATE_RANGE'" class="flex flex-col">
                  <ElInputDate v-model="(filterStatus[index] as DateFilterStatus).dateFrom" :hidden-error-message="true" />
                </div>

                <!-- FILTER: MULTIPLE VALUES -->
                <ElInputSelect
                  v-else-if="col.filter?.type === 'MULTI_VALUE' && filterStatus[index].type === 'MULTI_VALUE'"
                  v-model="(filterStatus[index] as TextFilterStatus).textSearch"
                  class="flex flex-col"
                  type="autocomplete"
                  :options="col.filter.selectOptions"
                  :hidden-error-message="true"
                />

                <!-- TODO: change with new button when it'll be available in DS version 2 -->
                <!-- FILTER: RESET -->

                <div v-else-if="col.filter?.type === 'RESET_FILTERS_BUTTON'" class="flex justify-end">
                  <ElButton label="Azzera filtri" variant="tertiary" size="xs" @click="resetFilters" />
                </div>
              </th>
            </tr>
          </thead>
          <CustomTransition name="fade">
            <tbody v-if="loading" key="loading">
              <tr>
                <td :colspan="props.columns.length + 1">
                  <ElSpinner label="Caricamento dati..." class="p-4" :extra-loading-msg-after-seconds="3" />
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="currentPageDataRows.length > 0" key="tableFull" class="bg-white">
              <tr
                v-for="row in currentPageDataRows"
                :key="row.id"
                class="transition duration-150 ease-in-out border-t border-neutral-surface"
                :class="{
                  'bg-neutral-surface-raised': rowsSelectionMode && selectedRows.find(x => x.id === row.id),
                }"
                data-cy="table-row"
              >
                <!-- ROWS SELECTION -->
                <td v-if="rowsSelectionMode" class="flex h-20 items-center justify-center py-3">
                  <ElInputCheckbox
                    :model-value="!!selectedRows.find(r => r.id === row.id)"
                    :disabled="rowsSelectionDisabled"
                    size="xxs"
                    @update:model-value="val => (val ? rowsSelected([row]) : rowUnselected(row.id))"
                  />
                </td>

                <!-- ///////////////////// DATA CELLS ///////////////////////// -->
                <td
                  v-for="(cell, i) in row.cells"
                  :key="i"
                  class="max-w-sm px-2.5 py-2.5"
                  :data-cy="cell.type === 'default' && cell.mainText.trim().replaceAll(' ', '').replaceAll(',', '')"
                >
                  <!-- TODO data-cy should stay inside ElTableCell -->
                  <ElTableCell :cell="cell" />
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="currentPageDataRows.length === 0" key="tableEmpty">
              <tr>
                <td :colspan="props.columns.length + 1" class="p-4 font-light text-neutral-darker">nessun dato trovato</td>
              </tr>
            </tbody>
          </CustomTransition>
        </table>
      </div>
    </div>

    <!-- Footer -->
    <ElServerSideTablePagination
      ref="pagination"
      :total-rows-count="totalRowsCount"
      :rows-per-page="props.rowsPerPage"
      :hide-footer="props.noFooter"
      @update:current-page="newPageSelected"
    />
  </div>
</template>
